import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllParents, deleteParent } from '../../../redux/parentRelated/parentHandle';
import { IconButton, Box, Typography, Paper, Container, Chip, Stack, Avatar, CircularProgress, Divider, Grid, Tooltip } from '@mui/material';
import { GreenButton } from '../../../components/buttonStyles';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonIcon from '@mui/icons-material/Person';
import SpeedDialTemplate from '../../../components/SpeedDialTemplate';
import { useToast } from '../../../context/ToastContext';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import styled, { keyframes } from 'styled-components';
import TableTemplate from '../../../components/TableTemplate';
import { underControl } from '../../../redux/parentRelated/parentSlice';

const ShowParents = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { parentList, loading, error, response } = useSelector((state) => state.parent);
    const { currentUser } = useSelector((state) => state.user);

    // Re-fetch every time this page is navigated to
    useEffect(() => {
        dispatch(getAllParents(currentUser._id));
        
        // Clear any previous status/response when entering this page
        return () => {
            dispatch(underControl());
        };
    }, [currentUser._id, dispatch, location.key]);

    const { showToast } = useToast();

    const deleteHandler = (deleteID, address) => {
        dispatch({ type: 'parent/getDeleteSuccess', payload: null });
        dispatch(deleteParent(deleteID, address)).then(() => {
            dispatch(getAllParents(currentUser._id));
            showToast("Parent record removed successfully", "success");
        });
    };

    const columns = [
        { id: 'photo', label: 'Photo', minWidth: 80 },
        { id: 'fatherName', label: 'Father Name', minWidth: 150 },
        { id: 'motherName', label: 'Mother Name', minWidth: 150 },
        { id: 'phone', label: 'Phone', minWidth: 120 },
        { id: 'email', label: 'Email', minWidth: 180 },
        { 
            id: 'students', 
            label: 'Linked Students', 
            minWidth: 200,
            render: (students) => {
                if (!students || students.length === 0) return <Chip label="No students linked" size="small" variant="outlined" sx={{ color: '#9ca3af', borderColor: '#e5e7eb' }} />;
                return (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {students.slice(0, 2).map((student, index) => (
                            <Chip
                                key={student._id || index}
                                icon={<SchoolIcon sx={{ fontSize: 14 }} />}
                                label={student.name}
                                size="small"
                                sx={{ mb: 0.5, bgcolor: '#f0f4ff', color: '#667eea', fontWeight: 500 }}
                            />
                        ))}
                        {students.length > 2 && (
                            <Chip
                                label={`+${students.length - 2} more`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 24, fontSize: '0.75rem' }}
                            />
                        )}
                    </Stack>
                );
            }
        },
    ];

    const rows = parentList.map((parent) => {
        return {
            photo: parent.photo,
            fatherName: parent.fatherName,
            motherName: parent.motherName || '-',
            phone: parent.phone || parent.fatherPhone || '-',
            email: parent.email || '-',
            students: parent.students || [],
            id: parent._id,
        };
    });

    const ParentButtonHaver = ({ row }) => {
        return (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <IconButton 
                    onClick={() => navigate("/Admin/parents/" + row.id)} 
                    title="View Parent" 
                    sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }} 
                    size="small"
                >
                    <VisibilityIcon color="primary" fontSize="small" />
                </IconButton>
                <IconButton 
                    onClick={() => deleteHandler(row.id, "Parent")} 
                    title="Delete Parent" 
                    sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }} 
                    size="small"
                >
                    <PersonRemoveIcon color="error" fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    if (loading) {
        return (
            <LoadingWrapper>
                <CircularProgress size={60} />
                <Typography sx={{ mt: 2, color: '#667eea', fontWeight: 600 }}>Loading Parents...</Typography>
            </LoadingWrapper>
        );
    }

    return (
        <PageContainer>
            <StickyHeader elevation={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2 }}>
                    <HeaderIconWrapper>
                        <GroupIcon sx={{ fontSize: 32 }} />
                    </HeaderIconWrapper>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            Parent Management
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            Manage parent profiles and student connections
                        </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Refresh">
                        <IconButton
                            onClick={() => dispatch(getAllParents(currentUser._id))}
                            sx={{ color: 'white', mr: 1, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <GreenButton
                        variant="contained"
                        onClick={() => navigate("/Admin/addparent")}
                        startIcon={<AddCircleIcon />}
                        sx={{ borderRadius: '12px', px: 3 }}
                    >
                        Add Parent
                    </GreenButton>
                </Box>
            </StickyHeader>

            <MainContent maxWidth="xl">
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={4}>
                        <StatCard elevation={2} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                            <StatIconWrapper>
                                <GroupIcon />
                            </StatIconWrapper>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h6" sx={{ opacity: 0.8, fontSize: '0.8rem', textTransform: 'uppercase' }}>Total Parents</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 800 }}>{rows.length}</Typography>
                            </Box>
                        </StatCard>
                    </Grid>
                </Grid>

                <SectionCard elevation={2}>
                    <SectionHeader>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Parent List</Typography>
                     </SectionHeader>
                    <Divider sx={{ mb: 3 }} />
                    
                    {(!loading && parentList.length === 0) ? (
                        <EmptyState>
                            <PersonIcon sx={{ fontSize: 64, color: '#e5e7eb', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary">No Parents Found</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>Get started by adding your first parent record.</Typography>
                            <GreenButton variant="contained" onClick={() => navigate("/Admin/addparent")}>
                                Add Parent
                            </GreenButton>
                        </EmptyState>
                    ) : (
                        <TableTemplate buttonHaver={ParentButtonHaver} columns={columns} rows={rows} />
                    )}
                </SectionCard>
            </MainContent>
            
        </PageContainer>
    );
};

export default ShowParents;

// Styled Components
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
    background-color: #f8fafc;
    min-height: 100vh;
`;

const StickyHeader = styled(Paper)`
    position: sticky;
    top: 0;
    z-index: 1000;
    border-radius: 0 !important;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%) !important;
    color: white !important;
`;

const HeaderIconWrapper = styled(Box)`
    background: rgba(255, 255, 255, 0.1);
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
`;

const MainContent = styled(Container)`
    padding-top: 32px;
    padding-bottom: 40px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const StatCard = styled(Paper)`
    padding: 24px;
    border-radius: 20px !important;
    color: white !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const StatIconWrapper = styled(Box)`
    background: rgba(255, 255, 255, 0.2);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    svg { font-size: 28px; }
`;

const SectionCard = styled(Paper)`
    padding: 32px;
    border-radius: 24px !important;
    background: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
`;

const SectionHeader = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const EmptyState = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    text-align: center;
`;

const LoadingWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 80vh;
`;
